import { FormConfig, Icon, Notice, Text } from '@lbcamden/lib-react';
import { DateTime } from 'luxon';
import { z } from 'zod';

import { pcfraActivityDisplayValues } from '~/client/config/display-values/pcfra';
import { presentOrPastDateSchema } from '~/common/schemas/data';
import { pcfraActivityTypeSchema } from '~/common/schemas/pcfra';
import { DisplayValuesMap } from '~/common/types/data';
import { PcfraActivityType } from '~/common/types/pcfra';
import { selectOptionsFromDisplayValues } from '~/common/utils/data';

import { commonFormConfig, commonFormSchema } from '../config';
import { rpeepActivityTypes } from './helpers';
import { DocumentSelectButton, DocumentUploadField } from './LogActivityDialog.styled';

export type LogActivityDefaultFormValues = z.infer<typeof defaultFormSchema>;

export type LogActivityRpeepFormValues = z.infer<typeof rpeepFormSchema>;

export type LogActivityUploadRpeepFormValues = z.infer<typeof uploadRpeepFormSchema>;

export type LogActivityFormValuesUnion =
  | LogActivityDefaultFormValues
  | LogActivityRpeepFormValues
  | LogActivityUploadRpeepFormValues;

export type LogActivityFormValuesIntersection = LogActivityDefaultFormValues &
  LogActivityRpeepFormValues &
  LogActivityUploadRpeepFormValues;

const defaultFormSchema = z
  .object({
    officerName: z.string(),
    date: presentOrPastDateSchema(),
    time: z.iso.time(),
    activityType: pcfraActivityTypeSchema,
  })
  .extend(commonFormSchema.shape)
  .refine(({ date, time }) => DateTime.fromISO(`${date}T${time}`) <= DateTime.now(), {
    message: 'Date and time cannot be in the future',
    path: ['time'],
  });

const rpeepFormSchema = z.object(defaultFormSchema.shape).omit({
  date: true,
  time: true,
  pcfraReviewDate: true,
});

const uploadRpeepFormSchema = rpeepFormSchema.extend({
  file: z.instanceof(File, { message: 'Please select the RPEEP document you wish to upload' }),
});

export const isDefaultFormValues = (
  formValues: LogActivityFormValuesUnion,
): formValues is LogActivityDefaultFormValues => 'date' in formValues && 'time' in formValues;

export const isRpeepFormValues = (
  formValues: LogActivityFormValuesUnion,
): formValues is LogActivityRpeepFormValues => rpeepActivityTypes.includes(formValues.activityType);

export const isUploadRpeepFormValues = (
  formValues: LogActivityFormValuesUnion,
): formValues is LogActivityUploadRpeepFormValues =>
  formValues.activityType === 'rpeep-completed' && 'file' in formValues;

export const selfReferralMessage =
  'Include a link to the self-referral in the comments, ' +
  'or a reference number that can be used to look it up';

export const getFormSchema = (selectedActivityType?: PcfraActivityType) => {
  switch (selectedActivityType) {
    case 'rpeep-completed':
      return uploadRpeepFormSchema;
    case 'rpeep-revoked':
      return rpeepFormSchema;
    default:
      return defaultFormSchema;
  }
};

export const getFormConfig = (
  hasSelectedSelfReferral?: boolean,
  canRevokeRpeep?: boolean,
): FormConfig<LogActivityFormValuesIntersection> => {
  const availableActivityDisplayValues = { ...pcfraActivityDisplayValues } as Partial<
    DisplayValuesMap<PcfraActivityType>
  >;

  if (!canRevokeRpeep) {
    delete availableActivityDisplayValues['rpeep-revoked'];
  }

  return {
    fieldConfigs: {
      ...commonFormConfig.fieldConfigs,
      officerName: {
        readOnly: true,
      },
      date: {
        label: 'Date of activity',
        type: 'date',
        inputProps: { max: DateTime.now().toISODate() },
      },
      time: {
        label: 'Time of activity',
        type: 'time',
      },
      activityType: {
        label: 'Type of activity',
        selectOptions: selectOptionsFromDisplayValues(
          availableActivityDisplayValues as DisplayValuesMap<PcfraActivityType>,
        ),
      },
      note: {
        ...commonFormConfig.fieldConfigs?.note,
        hideRequiredIndicator: true,
        label: hasSelectedSelfReferral ? (
          <div>
            Comments *
            <Notice
              css={(theme) => ({ marginTop: theme.spacing(1) })}
              infoWithIcon
              message={selfReferralMessage}
            />
          </div>
        ) : (
          'Comments *'
        ),
      },
      file: {
        label: 'Document',
        customRenderFn: (value, onChange, { key }) => (
          <DocumentUploadField>
            {value && <Text>{value.name}</Text>}
            <DocumentSelectButton tabIndex={-1} $variant="tertiary" $color="primary">
              <Icon icon="ui/upload" />
              Upload
              <input
                id={key}
                title="" // Remove default title
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const { files } = event.target;

                  if (files?.length) {
                    onChange(files[0]);
                  }
                }}
              />
            </DocumentSelectButton>
          </DocumentUploadField>
        ),
      },
    },
  };
};
